import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Pencil, Trash2, Save, X, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { teamAPI } from '../../services/api';

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try { const res = await teamAPI.getAll(); setMembers(res.data || []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const resetForm = () => { setEditing(null); setForm({}); };

  const startNew = () => {
    setEditing('new');
    setForm({ name: '', role: '', bio: '', photo: '', linkedin: '', order: members.length + 1 });
  };

  const startEdit = (m) => { setEditing(m._id); setForm({ ...m }); };

  const handleSave = async () => {
    if (!form.name || !form.role) { toast.error('Name and role are required'); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload._id;
    try {
      if (editing === 'new') { await teamAPI.create(payload); toast.success('Added'); }
      else { await teamAPI.update(editing, payload); toast.success('Updated'); }
      resetForm(); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove?')) return;
    try { await teamAPI.delete(id); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{members.length} team members</p>
        <Button onClick={startNew} data-testid="new-member-btn"><Plus className="w-4 h-4 mr-2" /> Add Member</Button>
      </div>

      {editing && (
        <Card className="mb-6 border-emerald-200" data-testid="team-form">
          <CardHeader><CardTitle>{editing === 'new' ? 'Add Member' : 'Edit Member'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1 block">Name *</label><Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Role *</label><Input value={form.role || ''} onChange={e => setForm({...form, role: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Photo URL</label><Input value={form.photo || ''} onChange={e => setForm({...form, photo: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">LinkedIn</label><Input value={form.linkedin || ''} onChange={e => setForm({...form, linkedin: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Order</label><Input type="number" value={form.order || ''} onChange={e => setForm({...form, order: e.target.value})} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Bio</label><Textarea rows={2} value={form.bio || ''} onChange={e => setForm({...form, bio: e.target.value})} /></div>
            <div className="flex gap-2">
              <Button onClick={handleSave}><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={resetForm}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {members.map(m => (
          <Card key={m._id}>
            <CardContent className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <img src={m.photo} alt={m.name} className="w-12 h-12 rounded-full object-cover border" />
                <div>
                  <h3 className="font-semibold text-gray-900">{m.name}</h3>
                  <p className="text-sm text-emerald-600">{m.role}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(m)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(m._id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TeamManager;
